import { defaultStaticRanges, createStaticRanges } from 'react-date-range';

/**
 * Given a date, returns Sunday 00:00:00 of that week.
 */
export const getSundayOfWeek = (d = new Date()) => {
  const date = new Date(d);
  const day = date.getDay();
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
};

/**
 * Given a date, returns Saturday 23:59:59 of that week.
 */
export const getSaturdayOfWeek = (d = new Date()) => {
  const sunday = getSundayOfWeek(d);
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  saturday.setHours(23, 59, 59, 999);
  return saturday;
};

/**
 * Formats a Date object into a string representation.
 * Default format: MM/DD/YYYY
 */
export const formatDateString = (dateInput, outputFormat = 'MM/DD/YYYY') => {
  if (!dateInput) return '';
  const dateInstance = new Date(dateInput);
  if (isNaN(dateInstance.getTime())) return String(dateInput);

  const year = dateInstance.getFullYear();
  const month = String(dateInstance.getMonth() + 1).padStart(2, '0');
  const day = String(dateInstance.getDate()).padStart(2, '0');

  if (outputFormat === 'YYYY-MM-DD') {
    return `${year}-${month}-${day}`;
  }
  return `${month}/${day}/${year}`;
};

/**
 * Calculates date range for 1 week ahead of current week (starting Sunday 1 week ahead).
 */
export const getTwoWeeksAheadRange = () => {
  const today = new Date();
  const currentWeekSunday = getSundayOfWeek(today);

  const nextWeekSunday = new Date(currentWeekSunday);
  nextWeekSunday.setDate(currentWeekSunday.getDate() + 7);

  const nextWeekSaturday = getSaturdayOfWeek(nextWeekSunday);

  return {
    startDate: nextWeekSunday,
    endDate: nextWeekSaturday,
    startStr: formatDateString(nextWeekSunday),
    endStr: formatDateString(nextWeekSaturday),
    rangeString: `${formatDateString(nextWeekSunday)} - ${formatDateString(nextWeekSaturday)}`,
  };
};

/**
 * Parses a range string ("MM/DD/YYYY - MM/DD/YYYY") into Date objects.
 */
export const parseRangeString = (rangeString) => {
  if (!rangeString || typeof rangeString !== 'string' || !rangeString.includes(' - ')) {
    return null;
  }
  const [startStr, endStr] = rangeString.split(' - ');
  const startDate = new Date(startStr);
  const endDate = new Date(endStr);
  if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
    return { startDate, endDate };
  }
  return null;
};

/**
 * Computes initial start and end dates based on input props.
 */
export const computeInitialDates = ({ startDate, endDate, value, defaultToPastWeek, minDateThreshold, customMinDate }) => {
  let start, end;
  if (startDate && endDate) {
    start = new Date(startDate);
    end = new Date(endDate);
  } else {
    const parsedRange = parseRangeString(value);
    if (parsedRange) {
      start = parsedRange.startDate;
      end = parsedRange.endDate;
    } else if (defaultToPastWeek) {
      const today = new Date();
      start = getSundayOfWeek(today);
      end = getSaturdayOfWeek(today);
    } else {
      const today = new Date();
      start = today;
      end = today;
    }
  }

  const activeMinThreshold = minDateThreshold || customMinDate;
  const isValidMinThreshold = activeMinThreshold instanceof Date && !isNaN(activeMinThreshold.getTime());
  if (isValidMinThreshold && start < activeMinThreshold) {
    start = new Date(activeMinThreshold);
  }

  return { start, end };
};

/**
 * Computes minDate and maxDate limits from props.
 */
export const computeMinMaxDates = ({
  disablePastDates,
  disablePastDateByWeekly,
  disablePastDatesByWeekly,
  disableFutureDates,
  customMinDate,
  customMaxDate,
  minDateThreshold,
  maxDateThreshold,
}) => {
  let minDate = minDateThreshold || customMinDate;
  if (!minDate) {
    if (disablePastDateByWeekly || disablePastDatesByWeekly) {
      minDate = getSundayOfWeek(new Date());
    } else if (disablePastDates) {
      const todayAtMidnight = new Date();
      todayAtMidnight.setHours(0, 0, 0, 0);
      minDate = todayAtMidnight;
    }
  }

  let maxDate = maxDateThreshold || customMaxDate;
  if (disableFutureDates && !maxDate) {
    maxDate = new Date();
  }

  return { minDate, maxDate };
};

/**
 * Computes custom Sunday-Saturday week static preset ranges.
 */
export const getCustomWeekStaticRanges = () => {
  return createStaticRanges([
    {
      label: 'This Week',
      range: () => ({
        startDate: getSundayOfWeek(new Date()),
        endDate: getSaturdayOfWeek(new Date()),
      }),
      isSelected(range) {
        const defined = this.range();
        return (
          formatDateString(range.startDate) === formatDateString(defined.startDate) &&
          formatDateString(range.endDate) === formatDateString(defined.endDate)
        );
      },
    },
    {
      label: 'Last Week',
      range: () => {
        const today = new Date();
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);
        return {
          startDate: getSundayOfWeek(lastWeek),
          endDate: getSaturdayOfWeek(lastWeek),
        };
      },
      isSelected(range) {
        const defined = this.range();
        return (
          formatDateString(range.startDate) === formatDateString(defined.startDate) &&
          formatDateString(range.endDate) === formatDateString(defined.endDate)
        );
      },
    },
  ]);
};

/**
 * Computes static preset ranges (filters out "This Month" and "Last Month" if disableMonthPresets is true).
 */
export const computeStaticRanges = (disableMonthPresets = true, customStaticRanges) => {
  if (customStaticRanges) {
    return customStaticRanges;
  }
  const baseRanges = getCustomWeekStaticRanges();
  if (!disableMonthPresets) {
    return [...baseRanges, ...defaultStaticRanges.filter((r) => r.label && r.label.toLowerCase().includes('month'))];
  }
  return baseRanges;
};

/**
 * Clamps date range selection to date thresholds and maxAllowedSpanInDays span.
 */
export const clampSelectionToDateThresholdsAndMaxSpan = (selection, maxAllowedSpanInDays, maxDateThreshold, minDateThreshold) => {
  if (!selection.startDate || !selection.endDate) {
    return selection;
  }
  let startDate = new Date(selection.startDate);
  let endDate = new Date(selection.endDate);

  const isValidMinThreshold = minDateThreshold instanceof Date && !isNaN(minDateThreshold.getTime());
  if (isValidMinThreshold && startDate < minDateThreshold) {
    startDate = new Date(minDateThreshold);
  }

  const isValidMaxThreshold = maxDateThreshold instanceof Date && !isNaN(maxDateThreshold.getTime());

  if (maxAllowedSpanInDays) {
    const selectionSpanInMs = endDate.getTime() - startDate.getTime();
    const selectedDaysSpan = Math.round(selectionSpanInMs / (1000 * 3600 * 24));

    if (selectedDaysSpan >= maxAllowedSpanInDays) {
      const cappedEndDate = new Date(startDate);
      cappedEndDate.setDate(startDate.getDate() + (maxAllowedSpanInDays - 1));

      return {
        ...selection,
        startDate,
        endDate: isValidMaxThreshold && cappedEndDate > maxDateThreshold ? new Date(maxDateThreshold) : cappedEndDate,
      };
    }
  }

  if (isValidMaxThreshold && endDate > maxDateThreshold) {
    endDate = new Date(maxDateThreshold);
  }

  return {
    ...selection,
    startDate,
    endDate,
  };
};

export const clampSelectionToMaxDays = clampSelectionToDateThresholdsAndMaxSpan;
